import { jsPDF } from 'jspdf';
import { getAccessToken } from './auth';

const MARGIN_X = 18;
const PAGE_WIDTH = 210; // A4 mm
const IMAGE_WIDTH = 175;
const LINE = 4;

function formatDate(raw) {
  if (!raw) return 'Not Set';
  // Parse as calendar date to avoid UTC midnight shifting the day
  const datePart = String(raw).split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return 'Not Set';
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${mm}/${dd}/${year}`;
}

function addBulletItem(doc, y, text) {
  const textX = MARGIN_X + 10;
  const maxWidth = PAGE_WIDTH - textX - 10;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(20);
  doc.text('•', MARGIN_X + 7, y + 1.5, { align: 'right' });
  doc.setFontSize(10);
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, textX, y + 1.5);
  return y + lines.length * 3 + 1;
}

async function loadHeaderImage() {
  const response = await fetch(`${process.env.PUBLIC_URL || ''}/oxr-header.png`);
  if (!response.ok) {
    throw new Error('Failed to load OxR header image');
  }
  const blob = await response.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const dims = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });

  return { dataUrl, dims };
}

/**
 * Builds the Overwatch x Rescue DTC confirmation letter.
 * Requires a valid MSAL access token — no query-param shortcut URL.
 * Pass a window opened synchronously from the click handler to avoid popup blockers.
 */
export async function openOxrConfirmationPdf(record, targetWindow = null) {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error('Authentication required to generate confirmation letter');
    }

    const oxrParents = ['OXR DTC', 'ONX MAPS'];
    if (!record || !oxrParents.includes(record.Parent)) {
      throw new Error('Confirmation letter is only available for OXR DTC and ONX MAPS members');
    }

    const username = `${record.First || ''} ${record.Last || ''}`.trim() || 'Member';
    const accountNumber = record.MembershipEXT || '';
    const effectiveDate = formatDate(record.Start);

    const { dataUrl: headerDataUrl, dims } = await loadHeaderImage();
    const imageHeight = IMAGE_WIDTH * (dims.height / dims.width);
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    let y = 10;
    const centerX = (PAGE_WIDTH - IMAGE_WIDTH) / 2;
    doc.addImage(headerDataUrl, 'PNG', centerX, y, IMAGE_WIDTH, imageHeight);
    // Match PHP: SetY(currentY + 25) after placing the header
    y = y + 25;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Dear ${username},`, MARGIN_X, y + 4);
    y += 10;

    const intro =
      'Thank you for purchasing the Overwatch x Rescue Emergency Assistance Plan. This letter serves as confirmation of your active coverage and rescue benefits.';
    const introLines = doc.splitTextToSize(intro, PAGE_WIDTH - MARGIN_X * 2);
    doc.text(introLines, MARGIN_X, y);
    y += introLines.length * LINE + 2;

    doc.setFont('helvetica', 'bold');
    doc.text('Plan Details', MARGIN_X, y + 4);
    y += 7;
    doc.setFont('helvetica', 'normal');

    y = addBulletItem(doc, y, 'Plan Name: Overwatch x Rescue DTC Emergency Assistance Plan');
    y = addBulletItem(doc, y, `Name on Plan: ${username}`);
    y = addBulletItem(doc, y, `Account Number: ${accountNumber}`);
    y = addBulletItem(doc, y, `Effective Date: ${effectiveDate}`);
    y = addBulletItem(doc, y, 'Plan Duration: 365 days');

    doc.setFont('helvetica', 'bold');
    doc.text('Coverage Benefits', MARGIN_X, y + 4);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.text('Your Overwatch x Rescue plan includes:', MARGIN_X, y + 4);
    y += 7;

    y = addBulletItem(doc, y, '24/7 multilingual emergency SOS assistance');
    y = addBulletItem(doc, y, 'Coordinated search and rescue (SAR) services');
    y = addBulletItem(doc, y, 'No-cost point of incident medical and security evacuation');
    y = addBulletItem(doc, y, 'Response to hazardous summer and winter sports incidents');
    y = addBulletItem(doc, y, 'Crisis consultation and emergency message transmission');
    y = addBulletItem(doc, y, 'Access to a global network of SAR agencies and resources');
    y = addBulletItem(
      doc,
      y,
      'Includes activities such as paragliding, mountaineering to heights above 3,000 meters, and rock climbing.'
    );

    doc.setFont('helvetica', 'bold');
    doc.text('Important Information:', MARGIN_X, y + 4);
    y += 7;
    doc.setFont('helvetica', 'normal');

    y = addBulletItem(doc, y, 'In case of emergency, activate the SOS feature on your connected device');
    y = addBulletItem(doc, y, 'For any questions or concerns, please contact us at: information@wwfocus.com');
    y += 4;

    const note1 =
      'Please note that all assistance is provided at the discretion of FocusPoint International and in accordance with the full terms and conditions of the Overwatch x Rescue plan.';
    const note1Lines = doc.splitTextToSize(note1, PAGE_WIDTH - MARGIN_X * 2);
    doc.text(note1Lines, MARGIN_X, y);
    y += note1Lines.length * LINE + 4;

    const note2 =
      'We hope you never need to use these services, but rest assured that should an emergency arise, Overwatch x Rescue is here to provide you with comprehensive assistance and peace of mind.';
    const note2Lines = doc.splitTextToSize(note2, PAGE_WIDTH - MARGIN_X * 2);
    doc.text(note2Lines, MARGIN_X, y);
    y += note2Lines.length * LINE + 4;

    doc.text('Sincerely,', MARGIN_X, y);
    y += 4;
    doc.text('The Overwatch x Rescue Team', MARGIN_X, y);

    const blobUrl = doc.output('bloburl');
    if (targetWindow && !targetWindow.closed) {
      targetWindow.location = blobUrl;
    } else {
      const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!win) {
        doc.save('oxr_confirmation.pdf');
      }
    }
  } catch (error) {
    if (targetWindow && !targetWindow.closed) {
      targetWindow.close();
    }
    throw error;
  }
}
